import TileLayer from 'ol/layer/Tile'
import TileWMS from 'ol/source/TileWMS'
import TileState from 'ol/TileState'
import type Tile from 'ol/Tile'
import type ImageTile from 'ol/ImageTile'
import { PROXY_BASE } from '../config'
import { ApiError, parseErrorResponse } from '../api/client'
import type { ErrorKind } from '../api/client'
import { gate } from './enforcement'

export type WmsTileLayer = TileLayer<TileWMS>

/**
 * Enforcement responses (429 seat, 403s, 503) surface through tile loads.
 * The handler is installed by the app shell and maps ApiError.kind to the
 * corresponding UI state (PRD P0.7).
 */
type EnforcementHandler = (err: ApiError) => void
let onEnforcement: EnforcementHandler = () => {}
export function setEnforcementHandler(handler: EnforcementHandler): void {
  onEnforcement = handler
}

/** Called on every successful tile so the 503 backoff can reset. */
type SuccessHandler = () => void
let onTileSuccess: SuccessHandler = () => {}
export function setTileSuccessHandler(handler: SuccessHandler): void {
  onTileSuccess = handler
}

/**
 * Retry schedule for transient tile failures: 3 retries after the first try.
 * OpenLayers never re-requests a tile whose loader set ERROR (its own
 * `ImageTile.load()` docstring says the tileLoadFunction must retry), and
 * `useInterimTilesOnError` then paints the previous zoom's tile, stretched, in
 * its place — the frozen blurry tile. So the retry has to live here.
 */
const RETRY_DELAYS_MS = [500, 1500, 4000]
/** ±15 %, so the tiles of one failed burst do not all come back in the same instant. */
const RETRY_JITTER = 0.15
/** A `Retry-After` beyond this is not a plausible hint; the schedule applies instead. */
const MAX_RETRY_AFTER_SEC = 30
/**
 * The longest server-requested wait actually honored, whatever the header says.
 * A retrying tile keeps one of OpenLayers' 16 tile-loading slots — shared with
 * the basemap — for its whole wait, so a long `Retry-After` must not pin it.
 * (503, which the real proxy sends with `Retry-After: 5`, is not retried here
 * at all — see RETRYABLE_STATUSES. 500/502/504 and network errors carry no
 * `Retry-After` from this proxy, so this cap does not bound them — see the
 * outage note on RETRYABLE_STATUSES below.)
 */
const MAX_HONORED_RETRY_AFTER_MS = 4000
/**
 * Retried by status. 429 is retried only when it is the edge rate-limit, never the seat limit.
 *
 * 503 is deliberately NOT retried here, although the fix's original brief listed
 * it — a measured, approved deviation (Pierre, 2026-09-22); do not add it back.
 *  - 503 is the one failure that already had map-wide recovery before tile
 *    retry existed: `handleEnforcement`'s `unavailable` case shows the toast and
 *    calls `gate.refreshAll()` after `Retry-After`. Network errors, the 429 edge
 *    rate-limit, 500/502/504 and 200-not-an-image had no recovery at all — that
 *    gap is what this retry fills.
 *  - Retrying 503 per tile was redundant with that, and worse during a full
 *    outage: every overlay tile (~96) sat in its retry wait holding one of
 *    OpenLayers' 16 loading slots, and the basemap could not get one. Measured,
 *    all GetMap answering 503 + `Retry-After: 5`: basemap 5/25 tiles at 10 s
 *    with 503 retried, still starved with the honored wait capped at 4 s, and
 *    25/25 at 2 s with 503 not retried — the pre-retry behaviour.
 *
 * ⚠️ KNOWN LIMITATION, knowingly accepted (Pierre, 2026-09-22): the same
 * starvation still happens for 500, 502 and network errors, which are NOT
 * excluded here. A genuine GeoServer-down or proxy-down outage answers with
 * exactly those — not 503, which this proxy sends only for a down Redis or
 * layer-policy store (see GeoserverAPI-CEO's `_forward_to_geoserver`, which
 * has no timeout/error handling around its `httpx` call, so a dead GeoServer
 * surfaces as a bare 500). Measured, all GetMap answering 500: basemap 5/25
 * tiles at 10 s, 24/25 at 30 s (502 and network-error behave the same) —
 * versus 25/25 at 2 s before this whole fix. During a real outage the
 * basemap now takes up to ~30 s instead of ~2 s to fill; the overlay layers
 * fail either way, retried or not. Accepted because such outages are rare
 * and the map still recovers, just slower — not solved further here. A
 * shared failure-streak circuit breaker (stop retrying new tiles once many
 * have failed in a row, reopen on the next success) would fix this properly
 * without weakening the retry for scattered failures; it was scoped but not
 * built — see the PR description for this branch.
 */
const RETRYABLE_STATUSES = new Set([500, 502, 504])

/** One failed attempt: what to log, and whether it deserves another try. */
interface TileFailure {
  kind: ErrorKind
  status: number
  message: string
  /** The proxy's error, for the enforcement UI. Absent for the two synthetic
   *  failures (network error, 2xx that is not an image): they carry no proxy
   *  decision, and `status: 0` must never reach `errorCode()` (= JS-CRASH). */
  apiErr?: ApiError
  retryable: boolean
  retryAfterSec: number | null
}

const NETWORK_FAILURE: TileFailure = {
  kind: 'unknown',
  status: 0,
  message: 'Network error (fetch failed)',
  retryable: true,
  retryAfterSec: null,
}

/**
 * `null` when the response is an image to paint. Otherwise the failure,
 * classified by HTTP status — not by `ApiError.kind`, which files 404 and 5xx
 * alike under 'unknown'. A 2xx is judged on its `Content-Type` *before* its
 * body is touched: GeoServer answers some errors with a 200 and an XML body.
 */
async function inspectTileResponse(res: Response): Promise<TileFailure | null> {
  if (res.ok) {
    const type = res.headers.get('Content-Type')
    if (type?.toLowerCase().startsWith('image/')) return null
    return {
      kind: 'unknown',
      status: res.status,
      message: `Tile response is not an image (Content-Type: ${type ?? 'missing'})`,
      retryable: true,
      retryAfterSec: null,
    }
  }
  const apiErr = await parseErrorResponse(res)
  const retryable =
    RETRYABLE_STATUSES.has(res.status) || (res.status === 429 && apiErr.kind === 'ratelimit')
  return {
    kind: apiErr.kind,
    status: apiErr.status,
    message: apiErr.message,
    apiErr,
    retryable,
    retryAfterSec: apiErr.retryAfterSec,
  }
}

/**
 * Delay before retry number `retry` (0-based): the server's `Retry-After` when
 * plausible, capped at MAX_HONORED_RETRY_AFTER_MS, else the schedule — jittered
 * either way, or a burst of tiles told the same `Retry-After` comes back in lockstep.
 *
 * `Retry-After: 0` falls back to the schedule: `parseErrorResponse` reads an
 * empty header as 0 too, so a 0 cannot be told apart from a malformed header,
 * and honoring it meant three back-to-back retries with no wait at all.
 */
function retryDelay(retry: number, retryAfterSec: number | null): number {
  const base =
    retryAfterSec !== null && retryAfterSec > 0 && retryAfterSec <= MAX_RETRY_AFTER_SEC
      ? Math.min(retryAfterSec * 1000, MAX_HONORED_RETRY_AFTER_MS)
      : RETRY_DELAYS_MS[retry]
  const jitter = 1 + (Math.random() * 2 - 1) * RETRY_JITTER
  return Math.round(base * jitter)
}

/**
 * `Disposable.disposed` is a plain public field at runtime but `protected` in
 * OL's type declarations. It flips when the tile cache evicts the tile (zoomed
 * away, `source.refresh()`), after which OL has dropped the tile's <img>.
 */
function isDisposed(tile: Tile): boolean {
  return (tile as unknown as { disposed: boolean }).disposed
}

/**
 * fetch-based tile loader (instead of plain <img>) so that:
 *  - the session cookie is sent cross-origin (credentials: 'include', PRD P0.2),
 *  - non-2xx responses can be read as the proxy's JSON error bodies,
 *  - tile traffic pauses while the seat-limit banner is up (no retry storm), and
 *  - a transient failure (network, 429 edge rate-limit, 500/502/504, 200 that is
 *    not an image) is retried before the tile is given up on. A 503 is not: the
 *    service toast's map-wide backoff recovers it (see RETRYABLE_STATUSES).
 *
 * While retrying, the tile is left in LOADING — the state OpenLayers put it in
 * before calling this loader — and its <img> is not touched: OL listens on that
 * <img> itself (`ImageTile.load()`), so assigning a `src` that fails to decode
 * would flip the tile to ERROR behind this loader's back. `img.src` is assigned
 * once, from a response already known to be an image.
 *
 * A failure the retry absorbs is not an incident: it is neither logged nor
 * handed to the enforcement UI. Only the final one is.
 */
function enforcedTileLoader(tile: Tile, src: string): void {
  const imageTile = tile as ImageTile

  const attempt = async (retry: number): Promise<void> => {
    // Evicted (zoomed away, source refreshed): nothing left to update.
    if (isDisposed(imageTile)) return
    // Seat limit up: stop here rather than hammer the proxy.
    if (gate.blocked) {
      imageTile.setState(TileState.ERROR)
      return
    }

    let blob: Blob | null = null
    let failure: TileFailure | null
    try {
      const res = await fetch(src, { credentials: 'include' })
      failure = await inspectTileResponse(res)
      if (!failure) blob = await res.blob()
    } catch {
      // fetch() rejected, or the connection dropped while the body was read.
      failure = NETWORK_FAILURE
    }
    // Disposed while in flight: OL has already dropped its <img>.
    if (isDisposed(imageTile)) return

    if (blob) {
      const url = URL.createObjectURL(blob)
      const img = imageTile.getImage() as HTMLImageElement
      img.onload = () => {
        URL.revokeObjectURL(url)
        onTileSuccess()
      }
      img.onerror = () => URL.revokeObjectURL(url)
      img.src = url
      return
    }
    if (!failure) return // unreachable: no failure means a blob was read

    if (failure.retryable && retry < RETRY_DELAYS_MS.length) {
      setTimeout(() => run(retry + 1), retryDelay(retry, failure.retryAfterSec))
      return
    }
    addLogEntry(failure.kind, failure.status, failure.message, src)
    if (failure.apiErr) onEnforcement(failure.apiErr)
    imageTile.setState(TileState.ERROR)
  }

  const run = (retry: number): void => {
    attempt(retry).catch(() => {
      // Safety net only — every expected failure is handled inside attempt().
      if (!isDisposed(imageTile)) imageTile.setState(TileState.ERROR)
    })
  }
  run(0)
}

import { addLogEntry } from '../ui/debugLog'
import { stackRank } from './layerNames'
import type { LayerInfo } from './capabilities'
import { VERSION_PARAM } from './layerVersions'

/**
 * Stacking of the business layers: the panel's order, upside down — see
 * `stackRank()`. Geology, the base, draws at 2 and the assets at 12; with a
 * shared z-index it was the order of the capabilities document that decided who
 * buried whom, and it buried the icons under the parcels, then the claims under
 * the geology. Every step stays below the vector twin (15), the labels (20) and
 * the highlight (25), which all belong on top of every basemap (0) and tile layer.
 */
const WMS_Z_BOTTOM = 2
const WMS_Z_STEP = 2

/**
 * Create a tiled WMS layer routed through the proxy (PRD P0.6). `version`, when
 * known at creation (`/api/layer-versions`), goes into the params as `V` so the
 * very first tiles already bypass whatever the browser cached. Later versions
 * arrive through `updateParams` (map/layerVersions.ts), which merges — it never
 * drops `V` — and `source.refresh()` keeps the params, so `V` survives both.
 */
export function createWmsLayer(info: LayerInfo, version?: string): WmsTileLayer {
  const source = new TileWMS({
    url: `${PROXY_BASE}/geoserver/${info.workspace}/wms`,
    params: {
      LAYERS: info.name,
      TILED: true,
      FORMAT: 'image/png',
      TRANSPARENT: true,
      ...(version ? { [VERSION_PARAM]: version } : {}),
    },
    transition: 0,
    tileLoadFunction: enforcedTileLoader,
  })
  gate.registerRefresher(() => source.refresh())
  const zIndex = WMS_Z_BOTTOM + WMS_Z_STEP * stackRank(info.name, info.title)
  const layer = new TileLayer({ source, visible: true, zIndex })
  layer.set('info', info)
  return layer
}

export function layerInfoOf(layer: WmsTileLayer): LayerInfo {
  return layer.get('info') as LayerInfo
}
